import React, { useState } from "react";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { isValidEmail, PROXY } from "../../lib/utils";
import { FaEye, FaEyeSlash } from "react-icons/fa"; // Import eye icons
import { apiErrorMessage, passwordIssue, passwordMessage } from "../../lib/passwordPolicy";

const resetPasswordSchema = (t) => Yup.object().shape({
    newPassword: Yup.string().test("password-policy", function (value) {
        const issue = passwordIssue(value);
        return issue ? this.createError({ message: passwordMessage(issue, t) }) : true;
    }),
});

const ResetPassword = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const resetLink = React.useRef(new URLSearchParams(window.location.hash.slice(1)));
    const legacyLink = React.useRef(new URLSearchParams(window.location.search));

    React.useEffect(() => {
        window.history.replaceState(null, "", "/reset-pwd");
    }, []);

    const [showPassword, setShowPassword] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        reset,
    } = useForm({
        mode: "onChange",
        resolver: yupResolver(resetPasswordSchema(t)),
    });

    const onSubmit = async (data) => {
        try {
            if (!navigator.onLine) {
                return toast.error(t("toast.internet-error"));
            }

            const email = resetLink.current.get("email") || legacyLink.current.get("email");
            const token = resetLink.current.get("token") || legacyLink.current.get("token");

            if (!isValidEmail(email) || !/^[a-f0-9]{64}$/i.test(token || "")) {
                return;
            }

            const response = await axios.put(`${PROXY}/auth/reset-password`, {
                email,
                token,
                newPassword: data.newPassword,
            });

            const { success } = response.data;

            if (success) {
                toast.success(t("resetSuccess"));
                navigate("/login");
                reset();
            }
        } catch (error) {
            toast.error(apiErrorMessage(error.response?.data, t));
        }
    };

    return (
        <section className="flex items-center justify-center h-screen">
            <form
                onSubmit={handleSubmit(onSubmit)}
                className="relative flex flex-col text-gray-700 bg-white shadow-md w-96 rounded-xl bg-clip-border mx-4"
            >
                <div className="relative text-center grid mx-4 mb-4 -mt-6 overflow-hidden text-white shadow-lg h-28 place-items-center rounded-xl bg-gradient-to-tr from-gray-900 to-gray-800 bg-clip-border shadow-gray-900/20">
                    <h3 className="block font-sans text-3xl antialiased font-semibold leading-snug tracking-normal text-white">
                        {t("reset-password")}
                    </h3>
                </div>
                <div className="flex flex-col gap-4 p-6">
                    {/* New Password Input */}
                    <div className="relative h-11 w-full min-w-[200px] mb-3 mt-1">
                        <input
                            type={showPassword ? "text" : "password"} // Toggle input type
                            {...register("newPassword")}
                            className={`w-full h-full px-3 py-3 font-sans text-sm font-normal transition-all bg-transparent border rounded-md peer border-t-transparent text-blue-gray-700 outline outline-0 placeholder-shown:border placeholder-shown:border-blue-gray-200 placeholder-shown:border-t-blue-gray-200 focus:border-2  focus:border-t-transparent focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50 ${errors.newPassword ? "focus:border-red-500" : "focus:border-gray-900"
                                }`}
                            placeholder=" "
                        />
                        <span
                            className="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer"
                            onClick={() => setShowPassword(!showPassword)} // Toggle password visibility
                        >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </span>
                        {errors.newPassword && (
                            <p className="text-xs text-red-500 px-1 my-1">{errors.newPassword.message}</p>
                        )}
                        <label
                            className={`mb-1 before:content[' '] after:content[' '] pointer-events-none absolute left-0 -top-1.5 flex h-full w-full select-none !overflow-visible truncate text-[11px] font-normal leading-tight text-gray-500 transition-all before:pointer-events-none before:mt-[6.5px] before:mr-1 before:box-border before:block before:h-1.5 before:w-2.5 before:rounded-tl-md before:border-t before:border-l before:border-transparent before:transition-all after:pointer-events-none after:mt-[6.5px] after:ml-1 after:box-border after:block after:h-1.5 after:w-2.5 after:flex-grow after:rounded-tr-md after:border-t after:border-r after:border-transparent after:transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:leading-[4.1] peer-placeholder-shown:text-blue-gray-500 peer-placeholder-shown:before:border-transparent peer-placeholder-shown:after:border-transparent peer-focus:text-[11px] peer-focus:leading-tight peer-focus:text-gray-900 peer-focus:before:border-t-2 peer-focus:before:border-l-2  peer-focus:after:border-t-2 peer-focus:after:border-r-2 peer-focus:after:!border-gray-900 peer-disabled:text-transparent peer-disabled:before:border-transparent peer-disabled:after:border-transparent peer-disabled:peer-placeholder-shown:text-blue-gray-500 ${errors.newPassword ? "peer-focus:text-red-500 peer-focus:after:!border-red-500 before:border-blue-gray-200" : ""
                                }`}
                        >
                            {t("new-password")}
                        </label>
                    </div>
                </div>
                <div className="p-6 pt-0">
                    <button
                        disabled={isSubmitting}
                        className="block w-full select-none rounded-lg bg-gradient-to-tr from-gray-900 to-gray-800 py-3 px-6 text-center align-middle font-sans text-xs font-bold uppercase text-white shadow-md shadow-gray-900/10 transition-all hover:shadow-lg hover:shadow-gray-900/20 active:opacity-[0.85] disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
                        type="submit"
                    >
                        {isSubmitting ? `${t("resetting")}...` : t("reset")}
                    </button>
                    <div className="flex justify-center">
                        <Link to="/sign-up" className="text-center text-blue-gray-800 text-sm font-semibold mt-3 hover:underline transition-shadow">
                            {t("dont_have_an_account")}
                        </Link>
                    </div>
                </div>
            </form>
        </section>
    );
};

export default ResetPassword;
